/**
 * ISO-3166-1 alpha-2, for the one place the storefront has to ask a customer where they are
 * (Design §10.1).
 *
 * **Codes here, names from `Intl`.** A hand-written table of country names is a table that goes
 * stale, renders English at a French customer, and turns a list of places into something somebody
 * has to maintain. The browser already holds the localised name for every one of these, so this
 * module holds the only part that is genuinely data: which codes exist.
 *
 * The list is **not** filtered by where the store is registered. `TaxTableService` decides that,
 * and it refuses with a sentence naming the country — which is a better answer than a picker that
 * silently omits somewhere the customer actually lives and leaves them with no selectable truth.
 */

/** Every officially assigned alpha-2 code, in code order. */
export const COUNTRY_CODES: readonly string[] = [
	'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
	'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS',
	'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN',
	'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE',
	'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF',
	'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM',
	'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM',
	'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC',
	'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK',
	'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA',
	'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG',
	'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
	'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
	'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO',
	'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
	'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW'
];

/**
 * One entry of the country dropdown: the ISO-3166 alpha-2 code the store sends and stores (`value`)
 * and the localised display name the customer reads (`name`).
 */
export interface CountryOption {
	value: string;
	name: string;
}

/**
 * The name of one country in the reader's language, falling back to the bare code.
 *
 * `Intl.DisplayNames` returns the code itself for anything it does not recognise, and it is absent
 * altogether on old engines — both of which land on the code, which is still a true answer.
 */
export function countryName(code: string, locale?: string): string {
	const upper = code.trim().toUpperCase();
	try {
		return new Intl.DisplayNames([locale ?? navigator.language], { type: 'region' }).of(upper) ?? upper;
	} catch {
		return upper;
	}
}

/**
 * Every country, named and sorted for the reader.
 *
 * Sorted with `Intl.Collator` rather than by `<`, because a plain string sort puts Åland after
 * Zimbabwe in every language that files it under A.
 */
export function countryOptions(locale?: string): CountryOption[] {
	const resolved = locale ?? (typeof navigator === 'undefined' ? 'en' : navigator.language);
	const collator = new Intl.Collator(resolved);
	return COUNTRY_CODES
		.map(value => ({ value, name: countryName(value, resolved) }))
		.sort((left, right) => collator.compare(left.name, right.name));
}

/**
 * A country's name and code, reduced to what a search should match on.
 *
 * Diacritics are stripped, because a picker that will not find "Aland" for `Åland` — or `Cote` for
 * `Côte d'Ivoire` — fails exactly the people whose own country carries the accent, and on a
 * keyboard that may not have it. `NFD` splits the letter from its mark so the mark can be dropped.
 */
export function searchKey(text: string): string {
	return text
		.normalize('NFD')
		.replace(/\p{Diacritic}/gu, '')
		.toLowerCase()
		.trim();
}

/**
 * The countries a query matches, in the order they should be offered.
 *
 * Matches the **name and the code**, because both are things a person types: somebody picking their
 * own country types its name, and somebody entering a tax rate types `DE`. A prefix match sorts
 * ahead of a match in the middle, so "in" offers India before Argentina — otherwise the ordinary
 * case is buried under alphabetically earlier countries that merely contain the letters.
 */
export function filterCountries(options: CountryOption[], query: string): CountryOption[] {
	const needle = searchKey(query);
	/* Nothing typed yet, so every country stays on offer. */
	if (needle === '') {
		return options;
	}

	const matched: { option: CountryOption; isPrefix: boolean }[] = [];
	/* Classify each country as a prefix match, a substring match or no match, in one pass. */
	for (const option of options) {
		const name = searchKey(option.name);
		const code = option.value.toLowerCase();
		if (name.startsWith(needle) || code.startsWith(needle)) {
			matched.push({ option, isPrefix: true });
		} else if (name.includes(needle)) {
			matched.push({ option, isPrefix: false });
		}
	}

	// A stable partition rather than a sort: the input is already in name order, and re-sorting
	// would throw that away for the entries inside each half.
	return [
		...matched.filter(entry => entry.isPrefix).map(entry => entry.option),
		...matched.filter(entry => !entry.isPrefix).map(entry => entry.option)
	];
}
